/**
 * Client-only blob URLs for in-flight canvas uploads. Never written to
 * Liveblocks — collaborators keep seeing skeleton nodes until the backend
 * places a real file record with a presigned URL.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

export type CanvasLocalFilePreview = {
  url: string;
  contentType: string;
};

type RegisterFn = (nodeId: string, file: File, contentType?: string) => void;
type RevokeFn = (nodeId: string) => void;

type CanvasLocalFilePreviewContextValue = {
  version: number;
  register: RegisterFn;
  revoke: RevokeFn;
  getPreview: (nodeId: string) => CanvasLocalFilePreview | undefined;
};

const CanvasLocalFilePreviewContext =
  createContext<CanvasLocalFilePreviewContextValue | null>(null);

function isPreviewableUploadMime(contentType: string): boolean {
  return contentType.startsWith("image/") || contentType.startsWith("video/");
}

export function CanvasLocalFilePreviewProvider({
  children,
}: {
  children: ReactNode;
}) {
  const previewsRef = useRef(new Map<string, CanvasLocalFilePreview>());
  const [version, setVersion] = useState(0);

  const register = useCallback<RegisterFn>((nodeId, file, contentType) => {
    const resolvedContentType =
      contentType || file.type || "application/octet-stream";
    if (!isPreviewableUploadMime(resolvedContentType)) return;

    const existing = previewsRef.current.get(nodeId);
    if (existing) {
      URL.revokeObjectURL(existing.url);
    }

    previewsRef.current.set(nodeId, {
      url: URL.createObjectURL(file),
      contentType: resolvedContentType,
    });
    setVersion((n) => n + 1);
  }, []);

  const revoke = useCallback<RevokeFn>((nodeId) => {
    const existing = previewsRef.current.get(nodeId);
    if (!existing) return;
    URL.revokeObjectURL(existing.url);
    previewsRef.current.delete(nodeId);
    setVersion((n) => n + 1);
  }, []);

  const getPreview = useCallback(
    (nodeId: string) => previewsRef.current.get(nodeId),
    [],
  );

  const value = useMemo(
    () => ({ version, register, revoke, getPreview }),
    [version, register, revoke, getPreview],
  );

  return (
    <CanvasLocalFilePreviewContext.Provider value={value}>
      {children}
    </CanvasLocalFilePreviewContext.Provider>
  );
}

export function useCanvasLocalFilePreview(
  nodeId: string,
): CanvasLocalFilePreview | undefined {
  const ctx = useContext(CanvasLocalFilePreviewContext);
  if (!ctx) return undefined;
  void ctx.version;
  return ctx.getPreview(nodeId);
}

export function useCanvasLocalFilePreviewActions(): {
  register: RegisterFn;
  revoke: RevokeFn;
} {
  const ctx = useContext(CanvasLocalFilePreviewContext);
  if (!ctx) {
    return {
      register: () => {},
      revoke: () => {},
    };
  }
  return { register: ctx.register, revoke: ctx.revoke };
}
